/**
 * Category-Picker Registry (Story 37.2 — Floating-Picker Framework)
 *
 * The single source of truth for the report-editor's right-sidebar floating
 * pickers. Each of the 8 sidebar categories maps to `{ panelComponent,
 * panelWidth }`; the pure helpers below derive EVERYTHING the route needs from
 * that one width — `shouldFloat`, `getPanelWidth`, and the canvas push
 * (`getPaperLeftShift`) + notch re-center (`getNotchMarginRight`).
 *
 * ADR-RE-2: one category-keyed framework, no per-category special cases; the
 * shift/margin are width-derived (no magic 140/282 that can desync). ADR-RE-6:
 * report-theme folds in as a registry entry (framework yes, schema no).
 *
 * Why a module object (not router state): React components are not
 * serializable, so the registry lives here as a plain const — never in
 * TanStack Router search params or a store. [Function-Registry pattern]
 *
 * SYNC NOTE: the registry is keyed to the 8 SIDEBAR ids ONLY — the 7 generic
 * sidebar categories (`SIDEBAR_CATEGORY_IDS` in WidgetPalette.tsx) + the
 * `report-theme` icon. The ~12 per-integration ids in `ACTIVE_CATEGORIES`
 * (instagram, meta-ads, ga4, …) are hidden (reachable via Platform Widgets
 * drill-in + Cmd+K) and MUST NOT float. If you add a sidebar icon, add it here.
 */

import type { WidgetType } from "./WidgetPalette";
import type { PresetWidget } from "../../lib/widgetPresets";
import { PICKER_PLATFORMS } from "../../lib/integrations";
import {
  IntegrationPickerPanel,
  PlatformWidgetCardsPanel,
  RollupGenericWidgetCardsPanel,
} from "./WidgetPalette";
import { ReportThemePanel } from "./ReportThemePanel";
import { ImagesCategoryPanel } from "./ImagesCategoryPanel";
import { EmbedsCategoryPanel } from "./EmbedsCategoryPanel";
import { ContentBlocksCategoryPanel } from "./ContentBlocksCategoryPanel";
import { CustomMetricsCategoryPanel } from "./CustomMetricsCategoryPanel";
import { BenchmarksCategoryPanel } from "./BenchmarksCategoryPanel";
import { GoalsCategoryPanel } from "./GoalsCategoryPanel";

// ── Panel props contract ─────────────────────────────────────────────

/**
 * Props every category panel receives from `FloatingCategoryPicker`. A given
 * panel reads only the fields it needs (e.g. the integrations panel uses the
 * `selectedIntegration` pair; the card-list panels use `clientIntegrations`).
 */
export interface CategoryPanelProps {
  /** The active sidebar category id (the panel may key its content off this). */
  activeCategory: string;
  /** Platform Widgets drill-in state — owned by the host (single source). */
  selectedIntegration: string | null;
  setSelectedIntegration: (id: string | null) => void;
  /** Full integration objects for resolving preset integrationId bindings. */
  clientIntegrations?: { id: string; type: string }[];
  /**
   * The report's EFFECTIVE client id (Story 18.4). The GoalsCategoryPanel reads
   * this to fetch the client's saved goals/budgets for its Saved mode; undefined
   * = the editor's unconnected/mock state, where the Saved segment is disabled.
   * All other panels ignore it (additive — no churn).
   */
  clientId?: string;
  /**
   * Add a widget to the canvas (Story 37.3). The configure-then-add category
   * panels (`ImagesCategoryPanel`, `EmbedsCategoryPanel`) call this with the
   * already-populated draft config so the new widget renders non-blank — they
   * never drop a default-blank widget. Optional because the card-list /
   * integrations panels add widgets through their own internals and ignore it.
   * Wired from the route's `handleAddWidget(type, initialConfig?)` (its
   * signature already accepts an optional initial config — no widening needed).
   *
   * Returns `true` when the widget was actually added, `false` when the add was
   * rejected (e.g. the page is at MAX_WIDGETS). The panels use this to decide
   * whether to clear their draft — a rejected add must NOT wipe the user's
   * just-configured upload/URL (code-review round 1).
   */
  onWidgetAdd?: (type: WidgetType, initialConfig?: Record<string, unknown>) => boolean;
  /**
   * Add MULTIPLE preset widgets in ONE atomic canvas update (Story 18.4 AC #9).
   * Looping `onWidgetAdd` is unsafe for a multi-widget add: the route plans each
   * widget against `activePageWidgetsRef.current`, which only refreshes on the
   * NEXT render — so N sequential single-adds all plan against the SAME stale
   * snapshot and collide on position. The dashboard preset therefore batches
   * through this single-update path (the `handlePresetAdd` precedent). Returns
   * `true` when the batch landed, `false` when rejected (not enough slots).
   */
  onPresetWidgetsAdd?: (widgets: PresetWidget[]) => boolean;
  /**
   * Story 40-6 (AC6) — true for a ROLL-UP report. The Platform-Widgets panel
   * reads this to swap the per-platform IntegrationPicker drill-in for a flat
   * aggregate-only generic-widget list (a roll-up has no single integration to
   * pick). Other panels ignore it (additive — the single-client flow is
   * byte-identical when false/undefined).
   */
  restrictToRollup?: boolean;
}

// ── Registry shape ───────────────────────────────────────────────────

export interface CategoryPickerConfig {
  /** Component rendered inside the floating panel for this category. */
  panelComponent: React.ComponentType<CategoryPanelProps>;
  /**
   * Panel width in px → drives BOTH the panel size AND the canvas push
   * (AC #3). All 8 categories use the standardized width today; a future
   * Phase-2 panel (37.3–37.6) that needs a different width sets its own value
   * here and the push auto-derives.
   *
   * NOTE: there is intentionally no max-width clamp — a much wider panel
   * (e.g. 320+) on a narrow viewport would produce a larger
   * `getNotchMarginRight` that could push the notch toward the right edge. At
   * 250 this is a non-issue; if Phase-2 introduces a wide panel, revisit a
   * clamp (YAGNI today — all panels are 250).
   */
  panelWidth: number;
}

// ── Width standardization + derivation constants ─────────────────────

/**
 * The single standardized panel width (AC #3 / ADR-RE-2). Resolves the old
 * split — 250 for the floating integrations picker vs 220 for the inline
 * card panels — in favor of 250.
 */
export const PICKER_PANEL_WIDTH = 250;

/**
 * Notch margin = panelWidth + this gutter. Today: 250 + 32 = 282 (pixel-
 * identical to the historical `marginRight: 282`). The gutter is the visual
 * gap between the panel's right edge and where the notch should re-center.
 */
const NOTCH_MARGIN_GUTTER = 32;

/**
 * Paper shift = round(panelWidth × this ratio). Today: round(250 × 0.56) = 140
 * (pixel-identical to the historical `paperLeftShift: 140`). The paper shifts
 * by only PART of the panel width because the canvas is centered and only
 * needs to clear the overlap, not the full panel.
 *
 * NOTE: the shift is `Math.round`ed, so `panelWidth` should stay an INTEGER
 * (all 250 today) — a fractional width would round to a near-pixel value. The
 * notch margin (additive) and the paper shift (multiplicative) are
 * deliberately DIFFERENT relationships (the notch is a full-width header strip;
 * the paper is centered content), tuned to look centered together at 250. If
 * Phase-2 (37.3–37.6) introduces a second width, re-check that the notch still
 * visually re-centers over the pushed paper and extend the registry test's
 * centering assertion accordingly.
 */
const PAPER_SHIFT_RATIO = 0.56;

// ── Panel-component wrappers ─────────────────────────────────────────

/**
 * Platform Widgets — the existing two-state drill-in flow
 * (IntegrationPicker ↔ PlatformWidgetCards). Reused VERBATIM (AC #6); only its
 * mount point moved from the inline sidebar to the floating picker.
 */
function IntegrationsMetricsPanel({
  selectedIntegration,
  setSelectedIntegration,
  restrictToRollup,
}: CategoryPanelProps) {
  // Story 40-6 (AC6): a roll-up has no single integration to drill into, so the
  // per-platform picker/preset flow is suppressed in favor of a flat
  // aggregate-only generic-widget list. The single-client flow below is unchanged.
  if (restrictToRollup) {
    return <RollupGenericWidgetCardsPanel />;
  }
  return selectedIntegration ? (
    <PlatformWidgetCardsPanel
      integrationId={selectedIntegration}
      platformName={
        PICKER_PLATFORMS.find((p) => p.platform === selectedIntegration)
          ?.displayName ?? selectedIntegration
      }
      onBack={() => setSelectedIntegration(null)}
    />
  ) : (
    <IntegrationPickerPanel
      selectedIntegration={selectedIntegration}
      onSelect={setSelectedIntegration}
    />
  );
}

// Story 37.5: the generic `CardListPanel` wrapper (which rendered
// `WidgetCardsPanel` for the card-list categories) was REMOVED here once its last
// three registry users — custom-metrics / benchmarks / goals — migrated to their
// own configure-then-add panels below (images/embeds left in 37.3, content-blocks
// in 37.4). It had ZERO remaining registry users and was eslint/tsc-flagged as
// dead, so it is gone rather than left as unused code. `WidgetCardsPanel` itself
// is UNTOUCHED — it still backs the inline-sidebar fallback in
// `WidgetPalette.tsx`; only this now-empty wrapper was dropped (AC #10).

// ── The registry ─────────────────────────────────────────────────────

/**
 * The 8 sidebar categories → their floating panel + width. Keyed to the
 * SIDEBAR ids ONLY (see SYNC NOTE in the file header).
 */
export const CATEGORY_PICKER_REGISTRY: Record<string, CategoryPickerConfig> = {
  "integrations-metrics": { panelComponent: IntegrationsMetricsPanel, panelWidth: PICKER_PANEL_WIDTH },
  // Story 37.4: content-blocks now uses a purpose-built configure-then-add panel
  // (was CardListPanel) with a four-way type selector. Width unchanged at
  // PICKER_PANEL_WIDTH so the 37.2 float/push math auto-derives identically.
  "content-blocks": { panelComponent: ContentBlocksCategoryPanel, panelWidth: PICKER_PANEL_WIDTH },
  // Story 37.3: images + embeds now use purpose-built configure-then-add panels
  // (was CardListPanel). Width unchanged at PICKER_PANEL_WIDTH so the 37.2
  // float/push math auto-derives identically — no width-derivation change.
  images: { panelComponent: ImagesCategoryPanel, panelWidth: PICKER_PANEL_WIDTH },
  embeds: { panelComponent: EmbedsCategoryPanel, panelWidth: PICKER_PANEL_WIDTH },
  // Story 37.5: custom-metrics / benchmarks / goals now use purpose-built
  // configure-then-add panels (was CardListPanel) reusing the shared sub-forms
  // moved to `widgetDataForms`. Width unchanged at PICKER_PANEL_WIDTH so the
  // 37.2 float/push math auto-derives identically. These were CardListPanel's
  // last three registry users — `CardListPanel` itself was REMOVED above (it had
  // zero remaining users and was eslint/tsc-flagged dead); `WidgetCardsPanel`,
  // which it wrapped, stays for the inline sidebar fallback in WidgetPalette so
  // nothing is dead repo-wide — AC #10.
  "custom-metrics": { panelComponent: CustomMetricsCategoryPanel, panelWidth: PICKER_PANEL_WIDTH },
  benchmarks: { panelComponent: BenchmarksCategoryPanel, panelWidth: PICKER_PANEL_WIDTH },
  goals: { panelComponent: GoalsCategoryPanel, panelWidth: PICKER_PANEL_WIDTH },
  "report-theme": { panelComponent: ReportThemePanel, panelWidth: PICKER_PANEL_WIDTH },
};

// ── Pure helpers — the single derivation point (AC #3) ───────────────
// The route calls these directly and does NO picker arithmetic of its own.

/** Look up a category's registry config (undefined for non-sidebar/unknown ids). */
export function getCategoryPickerConfig(
  category: string | null,
): CategoryPickerConfig | undefined {
  if (!category) return undefined;
  return CATEGORY_PICKER_REGISTRY[category];
}

/**
 * True for any registered sidebar category — the canonical "does this category
 * open a floating panel?" predicate. Replaces every
 * `activeCategory === "integrations-metrics"` conditional. False for null, the
 * ~12 per-integration ids, and any unknown id. `FloatingCategoryPicker` uses
 * this as its render guard, so it is the single floats-or-not authority.
 */
export function shouldFloat(category: string | null): boolean {
  return getCategoryPickerConfig(category) !== undefined;
}

/** The active panel's width, or 0 when no/non-floating category. */
export function getPanelWidth(category: string | null): number {
  return getCategoryPickerConfig(category)?.panelWidth ?? 0;
}

// ── Width → shift/margin (pure functions of width, the ONE derivation) ──
// Exported so the relationship can be regression-tested across widths (not
// just at the standardized 250), per code-review hardening. Both the
// category-keyed helpers below and the tests go through these.

/**
 * Canvas paper push (px) for a given panel width. round(width × ratio).
 * Non-positive / non-finite widths clamp to 0 (defensive — a registry width
 * should always be a positive integer, see the PAPER_SHIFT_RATIO note).
 */
export function paperShiftForWidth(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return 0;
  return Math.round(width * PAPER_SHIFT_RATIO);
}

/**
 * Notch header right-margin (px) for a given panel width. width + gutter.
 * Non-positive / non-finite widths clamp to 0 (defensive).
 */
export function notchMarginForWidth(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return 0;
  return width + NOTCH_MARGIN_GUTTER;
}

/**
 * The canvas paper push (px), derived from the active panel's width. 0 when the
 * category does not float. round(250 × 0.56) = 140 for the standardized width.
 * Gated on `shouldFloat` (registry presence), NOT on `width === 0`, so a future
 * registered-but-zero-width category can't masquerade as "not floating."
 */
export function getPaperLeftShift(category: string | null): number {
  if (!shouldFloat(category)) return 0;
  return paperShiftForWidth(getPanelWidth(category));
}

/**
 * The notch header right-margin (px), derived from the active panel's width. 0
 * when the category does not float. 250 + 32 = 282 for the standardized width.
 * Gated on `shouldFloat` (see getPaperLeftShift).
 */
export function getNotchMarginRight(category: string | null): number {
  if (!shouldFloat(category)) return 0;
  return notchMarginForWidth(getPanelWidth(category));
}
